import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, User, Trophy, Target, TrendingUp, Award, Zap, Users, Shield, Edit, Search, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { collection, onSnapshot, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

const PlayersPage = () => {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [playerStats, setPlayerStats] = useState({});
  const [recalculating, setRecalculating] = useState(false);
  const [recalculateSuccess, setRecalculateSuccess] = useState(false);

  useEffect(() => {
    const playersQuery = query(
      collection(db, 'players'),
      orderBy('name', 'asc')
    );

    const unsubscribePlayers = onSnapshot(playersQuery, (snapshot) => {
      const playersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPlayers(playersData);
      setLoading(false);
    });

    const unsubscribeTeams = onSnapshot(collection(db, 'teams'), (snapshot) => {
      const teamsData = {};
      snapshot.docs.forEach(doc => {
        teamsData[doc.id] = doc.data();
      });
      setTeams(teamsData);
    });

    return () => {
      unsubscribePlayers();
      unsubscribeTeams();
    };
  }, []);

  // Fetch and calculate real player statistics from fixtures
  useEffect(() => {
    const calculatePlayerStats = async () => {
      try {
        const fixturesSnapshot = await getDocs(collection(db, 'fixtures'));
        const stats = {};
        
        console.log('Total fixtures:', fixturesSnapshot.docs.length);
        const completedFixtures = fixturesSnapshot.docs.filter(doc => doc.data().status === 'completed');
        console.log('Completed fixtures:', completedFixtures.length);

        // Initialize stats for all players
        players.forEach(player => {
          stats[player.id] = {
            matchesPlayed: 0,
            wins: 0,
            losses: 0,
            aces: 0,
            unforcedErrors: 0,
            kickSmashes: 0,
            sportsmanshipTotal: 0,
            sportsmanshipCount: 0,
            sportsmanshipRating: 0
          };
        });

        // Calculate stats from completed fixtures
        fixturesSnapshot.docs.forEach(doc => {
          const fixture = doc.data();
          
          if (fixture.status === 'completed' && fixture.playerStats) {
            console.log('Processing completed fixture:', fixture.homeTeam, 'vs', fixture.awayTeam, 'Winner:', fixture.winner);
            // Process home team players
            if (fixture.playerStats.home) {
              Object.entries(fixture.playerStats.home).forEach(([playerKey, playerData]) => {
                const playerId = playerData.id;
                if (playerId && stats[playerId]) {
                  stats[playerId].matchesPlayed++;
                  stats[playerId].aces += playerData.aces || 0;
                  stats[playerId].unforcedErrors += playerData.unforcedErrors || 0;
                  stats[playerId].kickSmashes += playerData.kickSmashes || 0;
                  
                  if (playerData.sportsmanshipRating || playerData.akhlaaqRating) {
                    const rating = playerData.sportsmanshipRating || playerData.akhlaaqRating || 0;
                    stats[playerId].sportsmanshipTotal += rating;
                    stats[playerId].sportsmanshipCount++;
                  }
                  
                  // Determine win/loss
                  if (fixture.winner) {
                    if (fixture.winner === fixture.homeTeam) {
                      stats[playerId].wins++;
                    } else {
                      stats[playerId].losses++;
                    }
                  }
                }
              });
            }

            // Process away team players
            if (fixture.playerStats.away) {
              Object.entries(fixture.playerStats.away).forEach(([playerKey, playerData]) => {
                const playerId = playerData.id;
                if (playerId && stats[playerId]) {
                  stats[playerId].matchesPlayed++;
                  stats[playerId].aces += playerData.aces || 0;
                  stats[playerId].unforcedErrors += playerData.unforcedErrors || 0;
                  stats[playerId].kickSmashes += playerData.kickSmashes || 0;
                  
                  if (playerData.sportsmanshipRating || playerData.akhlaaqRating) {
                    const rating = playerData.sportsmanshipRating || playerData.akhlaaqRating || 0;
                    stats[playerId].sportsmanshipTotal += rating;
                    stats[playerId].sportsmanshipCount++;
                  }
                  
                  // Determine win/loss
                  if (fixture.winner) {
                    if (fixture.winner === fixture.awayTeam) {
                      stats[playerId].wins++;
                    } else {
                      stats[playerId].losses++;
                    }
                  }
                }
              });
            }
          }
        });

        // Calculate averages
        Object.keys(stats).forEach(playerId => {
          if (stats[playerId].sportsmanshipCount > 0) {
            stats[playerId].sportsmanshipRating = 
              stats[playerId].sportsmanshipTotal / stats[playerId].sportsmanshipCount;
          }
        });

        setPlayerStats(stats);
      } catch (error) {
        console.error('Error calculating player stats:', error);
      }
    };

    if (players.length > 0) {
      calculatePlayerStats();
    }
  }, [players]);

  const recalculatePlayerStats = async () => {
    if (!window.confirm('This will recalculate all player statistics from completed fixtures. Continue?')) {
      return;
    }

    setRecalculating(true);
    setRecalculateSuccess(false);

    try {
      console.log('Recalculating player stats...');
      
      const fixturesSnapshot = await getDocs(collection(db, 'fixtures'));
      const stats = {};
      
      const completedFixtures = fixturesSnapshot.docs.filter(doc => doc.data().status === 'completed');
      console.log(`Found ${completedFixtures.length} completed fixtures with player data`);

      // Initialize stats for all players
      players.forEach(player => {
        stats[player.id] = {
          matchesPlayed: 0,
          wins: 0,
          losses: 0,
          aces: 0,
          unforcedErrors: 0,
          kickSmashes: 0,
          sportsmanshipTotal: 0,
          sportsmanshipCount: 0,
          sportsmanshipRating: 0
        };
      });

      // Calculate stats from completed fixtures
      fixturesSnapshot.docs.forEach(doc => {
        const fixture = doc.data();
        
        if (fixture.status === 'completed' && fixture.playerStats) {
          console.log('✓ Processing:', fixture.homeTeam, 'vs', fixture.awayTeam);
          
          // Process home team players
          if (fixture.playerStats.home) {
            Object.entries(fixture.playerStats.home).forEach(([playerKey, playerData]) => {
              const playerId = playerData.id;
              if (playerId && stats[playerId]) {
                stats[playerId].matchesPlayed++;
                stats[playerId].aces += playerData.aces || 0;
                stats[playerId].unforcedErrors += playerData.unforcedErrors || 0;
                stats[playerId].kickSmashes += playerData.kickSmashes || 0;
                
                if (playerData.sportsmanshipRating || playerData.akhlaaqRating) {
                  const rating = playerData.sportsmanshipRating || playerData.akhlaaqRating || 0;
                  stats[playerId].sportsmanshipTotal += rating;
                  stats[playerId].sportsmanshipCount++;
                }
                
                if (fixture.winner && fixture.winner === fixture.homeTeam) {
                  stats[playerId].wins++;
                } else if (fixture.winner) {
                  stats[playerId].losses++;
                }
              }
            });
          }

          // Process away team players
          if (fixture.playerStats.away) {
            Object.entries(fixture.playerStats.away).forEach(([playerKey, playerData]) => {
              const playerId = playerData.id;
              if (playerId && stats[playerId]) {
                stats[playerId].matchesPlayed++;
                stats[playerId].aces += playerData.aces || 0;
                stats[playerId].unforcedErrors += playerData.unforcedErrors || 0;
                stats[playerId].kickSmashes += playerData.kickSmashes || 0;
                
                if (playerData.sportsmanshipRating || playerData.akhlaaqRating) {
                  const rating = playerData.sportsmanshipRating || playerData.akhlaaqRating || 0;
                  stats[playerId].sportsmanshipTotal += rating;
                  stats[playerId].sportsmanshipCount++;
                }
                
                if (fixture.winner && fixture.winner === fixture.awayTeam) {
                  stats[playerId].wins++;
                } else if (fixture.winner) {
                  stats[playerId].losses++;
                }
              }
            });
          }
        }
      });

      // Calculate averages
      Object.keys(stats).forEach(playerId => {
        if (stats[playerId].sportsmanshipCount > 0) {
          stats[playerId].sportsmanshipRating = 
            stats[playerId].sportsmanshipTotal / stats[playerId].sportsmanshipCount;
        }
      });

      setPlayerStats(stats);
      console.log('✅ Player stats recalculated successfully!');
      setRecalculateSuccess(true);
      setTimeout(() => setRecalculateSuccess(false), 3000);

    } catch (error) {
      console.error('Error recalculating player stats:', error);
      alert('Failed to recalculate player stats. Check console for details.');
    } finally {
      setRecalculating(false);
    }
  };

  const getPlayerStat = (player, stat) => {
    const stats = playerStats[player.id];
    if (!stats) return 0;
    
    switch(stat) {
      case 'matchesPlayed': return stats.matchesPlayed || 0;
      case 'wins': return stats.wins || 0;
      case 'losses': return stats.losses || 0;
      case 'winRate': 
        return stats.matchesPlayed > 0 ? (stats.wins / stats.matchesPlayed) * 100 : 0;
      case 'aces': return stats.aces || 0;
      case 'unforcedErrors': return stats.unforcedErrors || 0;
      case 'kickSmashes': return stats.kickSmashes || 0;
      case 'sportsmanship': return stats.sportsmanshipRating || 0;
      default: return 0;
    }
  };

  const filteredPlayers = players.filter(player => {
    // First apply status filter
    let passesStatusFilter = true;
    if (filter === 'assigned') passesStatusFilter = player.teamId;
    if (filter === 'unassigned') passesStatusFilter = !player.teamId;
    
    // Then apply search filter
    let passesSearchFilter = true;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      passesSearchFilter = 
        player.name?.toLowerCase().includes(query) ||
        player.position?.toLowerCase().includes(query) ||
        player.category?.toLowerCase().includes(query) ||
        teams[player.teamId]?.name?.toLowerCase().includes(query);
    }
    
    return passesStatusFilter && passesSearchFilter;
  });

  // Sort players based on selected criteria
  const sortedPlayers = [...filteredPlayers].sort((a, b) => {
    let aValue, bValue;
    
    if (sortBy === 'name') {
      aValue = a.name || '';
      bValue = b.name || '';
      return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    
    aValue = getPlayerStat(a, sortBy);
    bValue = getPlayerStat(b, sortBy);
    
    return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
  });

  const toggleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc'); // Default to descending for stats
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8"
        >
          <div>
            <h1 className="text-gradient" style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              Players
            </h1>
            <p className="text-gray-600" style={{ fontSize: '1.125rem' }}>
              Manage players and view statistics
            </p>
          </div>
          <Link
            to="/players/create"
            className="btn btn-primary flex items-center gap-2"
          >
            <Plus size={20} />
            Add Player
          </Link>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div style={{ display: 'flex', gap: '0.75rem', backgroundColor: 'white', borderRadius: '1rem', padding: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
            {[
              { key: 'all', label: 'All Players', icon: Users },
              { key: 'assigned', label: 'Team Players', icon: Trophy },
              { key: 'unassigned', label: 'Free Agents', icon: User }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                style={{
                  flex: 1,
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.3s',
                  background: filter === key ? 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)' : 'transparent',
                  color: filter === key ? 'white' : '#666',
                  boxShadow: filter === key ? '0 4px 12px rgba(28, 86, 61, 0.3)' : 'none',
                  transform: filter === key ? 'scale(1.05)' : 'scale(1)'
                }}
              >
                <Icon size={18} />
                {label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Recalculate Player Stats Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          style={{
            background: recalculateSuccess 
              ? 'linear-gradient(135deg, #D1FAE5 0%, #6EE7B7 100%)'
              : 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
            border: recalculateSuccess ? '2px solid #10B981' : '2px solid #F59E0B',
            borderRadius: '1rem',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '1.125rem', 
                fontWeight: 'bold', 
                color: recalculateSuccess ? '#065F46' : '#92400E',
                marginBottom: '0.5rem'
              }}>
                {recalculateSuccess ? '✅ Player Stats Recalculated!' : '⚙️ Recalculate Player Stats'}
              </div>
              <div style={{ fontSize: '0.875rem', color: recalculateSuccess ? '#065F46' : '#92400E' }}>
                {recalculateSuccess 
                  ? 'All player statistics have been updated from completed fixtures.'
                  : 'Update all player stats from completed matches (matches, wins, losses, aces, errors, smashes, sportsmanship)'}
              </div>
            </div>
            <motion.button
              whileHover={{ scale: recalculating ? 1 : 1.05 }}
              whileTap={{ scale: recalculating ? 1 : 0.95 }}
              onClick={recalculatePlayerStats}
              disabled={recalculating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1rem 2rem',
                background: recalculating 
                  ? 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)'
                  : 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.75rem',
                fontWeight: '700',
                fontSize: '1rem',
                cursor: recalculating ? 'not-allowed' : 'pointer',
                boxShadow: recalculating ? 'none' : '0 4px 12px rgba(28, 86, 61, 0.3)',
                whiteSpace: 'nowrap'
              }}
            >
              <RefreshCw 
                size={20} 
                className={recalculating ? 'animate-spin' : ''}
              />
              {recalculating ? 'Recalculating...' : 'Recalculate Now'}
            </motion.button>
          </div>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '1rem', 
            padding: '1rem', 
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            border: '2px solid #E5E7EB'
          }}>
            <div style={{ position: 'relative' }}>
              <Search 
                size={20} 
                style={{ 
                  position: 'absolute', 
                  left: '1rem', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  color: '#9CA3AF' 
                }} 
              />
              <input
                type="text"
                placeholder="Search players by name, position, category, or team..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem 0.75rem 3rem',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontSize: '1rem',
                  outline: 'none',
                  backgroundColor: '#F9FAFB'
                }}
              />
            </div>
            {searchQuery && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#6B7280' }}>
                Found {filteredPlayers.length} player{filteredPlayers.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </motion.div>

        {/* Stats Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            style={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <Users size={40} style={{ opacity: 0.8 }} />
              <TrendingUp size={24} style={{ opacity: 0.6 }} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>{players.length}</div>
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>Total Players</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <Trophy size={40} style={{ opacity: 0.8 }} />
              <TrendingUp size={24} style={{ opacity: 0.6 }} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {players.filter(p => p.teamId).length}
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>Team Players</div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.03, y: -4 }}
            style={{
              background: 'linear-gradient(135deg, #A855F7 0%, #9333EA 100%)',
              borderRadius: '1rem',
              padding: '1.5rem',
              color: 'white',
              boxShadow: '0 10px 30px rgba(168, 85, 247, 0.3)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <Zap size={40} style={{ opacity: 0.8 }} />
              <TrendingUp size={24} style={{ opacity: 0.6 }} />
            </div>
            <div style={{ fontSize: '2.25rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              {players.reduce((sum, p) => sum + (p.matchesPlayed || 0), 0)}
        </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '600' }}>Total Matches</div>
          </motion.div>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              >
                <User size={48} className="mx-auto mb-4 text-green-600" />
              </motion.div>
              <p className="text-gray-600 font-semibold">Loading players...</p>
            </div>
          </div>
        ) : filteredPlayers.length > 0 ? (
          <>
            {/* Player Stats Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div style={{
                backgroundColor: 'white',
                borderRadius: '1rem',
                padding: '1.5rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}>
                <h3 style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 'bold', 
                  color: '#1C563D', 
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <Target size={24} />
                  Player Statistics Table
                </h3>
                
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#F8FAFC' }}>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                            Player
                            {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>Team</th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('matchesPlayed')}>
                            Played
                            {sortBy === 'matchesPlayed' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('wins')}>
                            W
                            {sortBy === 'wins' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('losses')}>
                            L
                            {sortBy === 'losses' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('winRate')}>
                            W/L%
                            {sortBy === 'winRate' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('aces')}>
                            Aces
                            {sortBy === 'aces' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('unforcedErrors')}>
                            Errors
                            {sortBy === 'unforcedErrors' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('kickSmashes')}>
                            Smashes
                            {sortBy === 'kickSmashes' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer' }} onClick={() => toggleSort('sportsmanship')}>
                            Sportsmanship
                            {sortBy === 'sportsmanship' && (sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                          </div>
                        </th>
                        <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '2px solid #E5E7EB' }}>Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPlayers.map((player, index) => {
                        const team = player.teamId ? teams[player.teamId] : null;
                        const stats = playerStats[player.id] || {};
                        const isTop3 = sortBy !== 'name' && index < 3;
                        const winRate = stats.matchesPlayed > 0 ? ((stats.wins / stats.matchesPlayed) * 100).toFixed(1) : '0.0';
                        
                        return (
                          <tr key={player.id} style={{ 
                            backgroundColor: isTop3 ? 
                              (index === 0 ? '#FEF3C7' : index === 1 ? '#E5E7EB' : '#FEE2E2') :
                              (index % 2 === 0 ? 'white' : '#F8FAFC'),
                            transition: 'background-color 0.2s'
                          }}>
                            <td style={{ padding: '0.75rem', borderBottom: '1px solid #E5E7EB' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                {isTop3 && (
                                  <div style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    background: index === 0 ? 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)' :
                                                index === 1 ? 'linear-gradient(135deg, #D1D5DB 0%, #9CA3AF 100%)' :
                                                'linear-gradient(135deg, #FECACA 0%, #F87171 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                                  }}>
                                    #{index + 1}
                                  </div>
                                )}
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem'
                                }}>
                                  {player.name?.charAt(0) || 'P'}
                    </div>
                    <div>
                                  <div style={{ fontWeight: '600', color: '#1F2937' }}>{player.name}</div>
                                  <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>{player.position || 'Player'}</div>
                    </div>
                  </div>
                            </td>
                            <td style={{ padding: '0.75rem', borderBottom: '1px solid #E5E7EB' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                {team?.logo && (
                                  <img 
                                    src={team.logo} 
                                    alt={team.name} 
                                    style={{ width: '20px', height: '20px', borderRadius: '4px', objectFit: 'cover' }}
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                )}
                                <span style={{ color: '#374151', fontWeight: '500' }}>
                                  {team?.name || 'Unassigned'}
                                </span>
                  </div>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#DBEAFE', 
                                color: '#1E40AF', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {stats.matchesPlayed || 0}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#D1FAE5', 
                                color: '#065F46', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {stats.wins || 0}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#FEE2E2', 
                                color: '#DC2626', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {stats.losses || 0}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#DBEAFE', 
                                color: '#1E40AF', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {winRate}%
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#D1FAE5', 
                                color: '#065F46', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {stats.aces || 0}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#FEE2E2', 
                                color: '#DC2626', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {stats.unforcedErrors || 0}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#FEF3C7', 
                                color: '#92400E', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {stats.kickSmashes || 0}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#F0FDF4', 
                                color: '#059669', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {stats.sportsmanshipRating ? stats.sportsmanshipRating.toFixed(1) : '0.0'}
                              </span>
                            </td>
                            <td style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #E5E7EB' }}>
                              <span style={{ 
                                background: '#F3E8FF', 
                                color: '#7C3AED', 
                                padding: '0.25rem 0.5rem', 
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontWeight: '600'
                              }}>
                                {player.category || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>

            {/* Player Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPlayers.map((player, index) => {
              const team = player.teamId ? teams[player.teamId] : null;
              const stats = playerStats[player.id] || {};
              
              return (
                <motion.div
                  key={player.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.03, y: -6 }}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '1rem',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s'
                  }}
                >
                  {/* Player Header with Image */}
                  <div style={{
                    background: 'linear-gradient(135deg, #3B82F6 0%, #10B981 100%)',
                    padding: '1.5rem',
                    paddingBottom: '3rem',
                    position: 'relative'
                  }}>
                    <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                      <Link
                        to={`/players/${player.id}/edit`}
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(8px)',
                          padding: '0.5rem',
                          borderRadius: '0.5rem',
                          display: 'inline-block',
                          transition: 'all 0.3s'
                        }}
                      >
                        <Edit size={16} style={{ color: 'white' }} />
                      </Link>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: '96px',
                        height: '96px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        backgroundColor: 'white',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                        border: '4px solid rgba(255, 255, 255, 0.5)',
                        marginBottom: '0.75rem'
                      }}>
                        {player.profileImage ? (
                          <img
                            src={player.profileImage}
                            alt={`${player.name} profile`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(135deg, #60A5FA 0%, #34D399 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <User size={40} style={{ color: 'white' }} />
                    </div>
                  )}
                </div>

                      <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'white', textAlign: 'center', marginBottom: '0.25rem' }}>
                        {player.name}
                      </h3>
                      
                      {team ? (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(8px)',
                          padding: '0.375rem 0.75rem',
                          borderRadius: '9999px'
                        }}>
                          {team.logo ? (
                            <img src={team.logo} alt={team.name} style={{ width: '20px', height: '20px', borderRadius: '50%' }} />
                          ) : (
                            <Shield size={14} style={{ color: 'white' }} />
                          )}
                          <span style={{ color: 'white', fontSize: '0.875rem', fontWeight: '600' }}>{team.name}</span>
                        </div>
                      ) : (
                        <span style={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '9999px'
                        }}>
                          Free Agent
                        </span>
                      )}
                </div>
              </div>

                  {/* Player Stats */}
                  <div style={{ padding: '1.25rem', marginTop: '-1.5rem', position: 'relative', zIndex: 10 }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '0.75rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', padding: '1rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <div style={{
                          textAlign: 'center',
                          padding: '0.75rem',
                          background: 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2563EB' }}>
                            {stats.matchesPlayed || 0}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: '600' }}>Matches</div>
        </div>

                        <div style={{
                          textAlign: 'center',
                          padding: '0.75rem',
                          background: 'linear-gradient(135deg, #D1FAE5 0%, #6EE7B7 100%)',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669' }}>
                            {stats.aces || 0}
          </div>
                          <div style={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: '600' }}>Aces</div>
                </div>

                        <div style={{
                          textAlign: 'center',
                          padding: '0.75rem',
                          background: 'linear-gradient(135deg, #E9D5FF 0%, #C084FC 100%)',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9333EA' }}>
                            {stats.kickSmashes || 0}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: '600' }}>Smashes</div>
                </div>

                        <div style={{
                          textAlign: 'center',
                          padding: '0.75rem',
                          background: 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)',
                          borderRadius: '0.5rem'
                        }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#D97706' }}>
                            {stats.sportsmanshipRating ? `${stats.sportsmanshipRating.toFixed(1)}/5` : '0.0/5'}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#4B5563', fontWeight: '600' }}>Rating</div>
                        </div>
                      </div>
                </div>

                    {/* Additional Info */}
                    {(player.playtomicRating || player.category) && (
                      <div className="flex gap-2 mb-4">
                        {player.playtomicRating && (
                          <div className="flex-1 bg-gray-50 p-2.5 rounded-lg text-center border border-gray-200">
                            <div className="text-xs text-gray-500 font-semibold mb-0.5">Playtomic</div>
                            <div className="font-bold text-gray-800">{player.playtomicRating}</div>
                          </div>
                        )}
                        {player.category && (
                          <div className="flex-1 bg-gray-50 p-2.5 rounded-lg text-center border border-gray-200">
                            <div className="text-xs text-gray-500 font-semibold mb-0.5">Tier</div>
                            <div className="font-bold text-gray-800">{player.category}</div>
                          </div>
                        )}
                </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Link
                        to={`/players/${player.id}`}
                        className="btn btn-secondary flex-1 text-center text-sm py-2.5"
                      >
                        View Profile
                      </Link>
                      <Link
                        to={`/players/${player.id}/edit`}
                        className="btn btn-primary flex-1 text-center text-sm py-2.5"
                      >
                        Edit
                      </Link>
                    </div>
                </div>
            </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="bg-white rounded-2xl p-12 shadow-lg inline-block">
              <User size={64} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-2xl font-bold text-gray-700 mb-2">
                {filter === 'all' ? 'No Players Yet' : `No ${filter} Players`}
              </h3>
              <p className="text-gray-600 mb-6">
                {filter === 'all' 
                  ? 'Add players to start building your league'
                  : `No players found in this category`
                }
              </p>
              <Link
                to="/players/create"
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <Plus size={20} />
                Add Player
              </Link>
            </div>
          </motion.div>
        )}
          </>
        ) : (
            <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <User size={64} className="mx-auto mb-4 text-gray-400" />
            <h3 className="text-2xl font-bold text-gray-700 mb-2">No Players Found</h3>
            <p className="text-gray-600 mb-6">
              {filter === 'all' 
                ? 'No players have been created yet'
                : `No ${filter} players found`
              }
            </p>
            <Link
              to="/players/create"
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Add Player
            </Link>
            </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlayersPage;
